import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

interface NavItem {
  href: string;
  label: string;
  active?: boolean;
}

interface MobileMenuProps {
  navItems: NavItem[];
  ctaHref: string;
  ctaLabel: string;
}

export default function MobileMenu({ navItems, ctaHref, ctaLabel }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [location] = useLocation();

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <div className="md:hidden">
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 rounded-md text-foreground hover:bg-secondary/50 transition-colors"
        aria-label="Open menu"
        aria-expanded={isOpen}
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Portal: render overlay outside nav stacking context */}
      {isOpen &&
        createPortal(
          <div
            className="fixed inset-0"
            style={{ zIndex: 9999 }}
          >
            {/* Dark backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setIsOpen(false)}
            />

            {/* Slide-in Panel */}
            <div
              className="absolute top-0 right-0 h-full w-[280px] max-w-[85vw] shadow-2xl overflow-y-auto"
              style={{ backgroundColor: '#ffffff', zIndex: 10000 }}
            >
              {/* Close button */}
              <div className="flex justify-end p-4 pt-5">
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex flex-col h-[calc(100%-72px)] px-6 pb-8">
                {/* Navigation Links */}
                <nav className="flex flex-col gap-1 flex-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        item.active
                          ? "bg-teal-50 text-teal-700"
                          : "text-gray-800 hover:bg-gray-100 hover:text-teal-700"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </nav>

                {/* Language Switcher */}
                <div className="border-t border-gray-200 pt-5 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 px-4">
                    Language
                  </p>
                  <div className="px-4">
                    <LanguageSwitcher />
                  </div>
                </div>

                {/* CTA Button */}
                <Link
                  href={ctaHref}
                  className="mx-4 py-3 bg-teal-600 text-white text-center rounded-lg font-medium hover:bg-teal-700 transition-colors"
                >
                  {ctaLabel}
                </Link>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
